import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { useDebounce } from 'hooks/useDebounce';
import { IHotelCollectionFilters } from '../List';

interface IUrlFilterParams {
  name?: string;
  countryCodes?: string; // Comma-separated in URL
  bestFors?: string; // Comma-separated in URL
}

/**
 * Parse comma-separated string to array, handling edge cases
 */
export const parseCommaSeparated = (value: string | undefined): string[] => {
  if (!value || value.trim() === '') return [];
  return value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
};

/**
 * Serialize array to comma-separated string for URL
 * Returns undefined if array is empty (to omit from URL)
 */
export const serializeToComma = (arr: string[]): string | undefined => {
  return arr.length > 0 ? arr.join(',') : undefined;
};

/**
 * Update URL with filter values using replaceState (no history pollution)
 */
const updateUrl = (filters: IHotelCollectionFilters) => {
  const params: Record<string, string | undefined> = {
    name: filters.name || undefined,
    countryCodes: serializeToComma(filters.countryCodes),
    bestFors: serializeToComma(filters.bestFors),
  };

  // Remove undefined values
  const cleanParams = Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined));

  const queryString = qs.stringify(cleanParams);
  const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

  window.history.replaceState({}, '', newUrl);
};

/**
 * Hook for managing Hotel Collection filters with URL synchronization
 *
 * Features:
 * - Initializes filter values from URL query parameters on mount
 * - Syncs filter changes to URL using replaceState (doesn't pollute history)
 * - Debounces name search (1000ms) before updating URL
 * - Array filters (countryCodes, bestFors) update URL immediately
 * - Provides applyFilters() for bulk updates (mobile drawer)
 */
export function useHotelCollectionUrlFilters() {
  const location = useLocation();
  const isInitialMount = useRef(true);

  // Parse initial values from URL
  const getInitialFilters = useCallback((): IHotelCollectionFilters => {
    const search = location.search.replace(/^\?/, '');
    if (!search) {
      return { name: '', countryCodes: [], bestFors: [] };
    }

    const parsed = qs.parse(search) as IUrlFilterParams;
    return {
      name: parsed.name || '',
      countryCodes: parseCommaSeparated(parsed.countryCodes),
      bestFors: parseCommaSeparated(parsed.bestFors),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, don't react to location.search changes

  const initialFilters = getInitialFilters();

  // Array filters - initialize from URL
  const [countryCodes, setCountryCodesState] = useState<string[]>(initialFilters.countryCodes);
  const [bestFors, setBestForsState] = useState<string[]>(initialFilters.bestFors);

  // Use refs to get latest array values in debounce callback (avoids stale closures)
  const countryCodesRef = useRef(countryCodes);
  const bestForsRef = useRef(bestFors);

  useEffect(() => {
    countryCodesRef.current = countryCodes;
  }, [countryCodes]);

  useEffect(() => {
    bestForsRef.current = bestFors;
  }, [bestFors]);

  // Name with debounce - update URL after debounce settles
  const [name, setName, debouncedName, setDebouncedName] = useDebounce(
    initialFilters.name,
    1000,
    // onChange callback - fires when debounced value changes
    (newName: string) => {
      if (!isInitialMount.current) {
        updateUrl({
          name: newName,
          countryCodes: countryCodesRef.current,
          bestFors: bestForsRef.current,
        });
      }
    }
  );

  // Mark initial mount complete after first render
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  // Wrapped setters that also update URL immediately
  const setCountryCodes = useCallback(
    (newCodes: string[]) => {
      setCountryCodesState(newCodes);
      updateUrl({ name: debouncedName, countryCodes: newCodes, bestFors: bestForsRef.current });
    },
    [debouncedName]
  );

  const setBestFors = useCallback(
    (newBestFors: string[]) => {
      setBestForsState(newBestFors);
      updateUrl({ name: debouncedName, countryCodes: countryCodesRef.current, bestFors: newBestFors });
    },
    [debouncedName]
  );

  // Bulk apply filters (for mobile drawer Apply button)
  const applyFilters = useCallback(
    (filters: IHotelCollectionFilters) => {
      setDebouncedName(filters.name); // Sets both immediate and debounced values
      setCountryCodesState(filters.countryCodes);
      setBestForsState(filters.bestFors);
      updateUrl(filters);
    },
    [setDebouncedName]
  );

  // Reset all filters
  const resetFilters = useCallback(() => {
    const emptyFilters: IHotelCollectionFilters = { name: '', countryCodes: [], bestFors: [] };
    setDebouncedName('');
    setCountryCodesState([]);
    setBestForsState([]);
    updateUrl(emptyFilters);
  }, [setDebouncedName]);

  return {
    // Values
    name,
    debouncedName,
    countryCodes,
    bestFors,
    // Setters
    setName,
    setDebouncedName,
    setCountryCodes,
    setBestFors,
    // Bulk operations
    applyFilters,
    resetFilters,
    currentFilters: { name, countryCodes, bestFors } as IHotelCollectionFilters,
  };
}
